import type { CategorizedProcesses, ProcessDefinition } from '@/lib/camunda/types';

const DEFAULT_CATEGORY = 'Sin Categoría';

const KNOWN_PROCESS_NAMESPACES = new Set([
  'laboral',
  'comercial',
  'rrhh',
  'contables',
  'ventas',
  'compras',
  'finanzas',
  'test',
]);

const CATEGORY_LABELS = new Map([
  ['laboral', 'Laboral'],
  ['comercial', 'Comercial'],
  ['rrhh', 'RRHH'],
  ['contables', 'Contables'],
  ['ventas', 'Ventas'],
  ['compras', 'Compras'],
  ['finanzas', 'Finanzas'],
  ['test', 'Test'],
]);

const FORM_KEY_ALIASES = new Map([
  ['gestion-comercial', 'gestion-clientes'],
]);

const EXCLUDED_PROCESS_DEFINITION_KEYS = new Set([
  'lleida-mailcert-test',
  'comercial-gestion-cliente',
  'comercial-alta-propuesta',
  'alta-propuesta',
  'test-rpa-trabajador',
  'test-chat',
  'test-rpa-produccion',
]);

function isProcessDefinitionStartable(process: Pick<ProcessDefinition, 'key'>): boolean {
  return !EXCLUDED_PROCESS_DEFINITION_KEYS.has(normalizeKey(process.key));
}

export function filterStartableProcessDefinitions(
  processes: ProcessDefinition[],
): ProcessDefinition[] {
  return processes.filter(isProcessDefinitionStartable);
}

const normalizeKey = (value: string): string => value.trim().toLowerCase();

function getNamespaceFromProcessKey(processKey: string): string | null {
  if (!processKey) return null;
  const normalized = normalizeKey(processKey);
  const [namespace] = normalized.split('-', 1);
  return namespace || null;
}

function getCategoryLabel(namespace: string): string {
  return CATEGORY_LABELS.get(namespace) ?? namespace.charAt(0).toUpperCase() + namespace.slice(1);
}

export function categorizeProcesses(processes: ProcessDefinition[]): CategorizedProcesses {
  const categorized: CategorizedProcesses = {
    [DEFAULT_CATEGORY]: [],
  };

  filterStartableProcessDefinitions(processes).forEach((process) => {
    const namespace = getNamespaceFromProcessKey(process.key);

    if (!namespace || !KNOWN_PROCESS_NAMESPACES.has(namespace)) {
      categorized[DEFAULT_CATEGORY].push(process);
      return;
    }

    const category = getCategoryLabel(namespace);
    if (!categorized[category]) {
      categorized[category] = [];
    }
    categorized[category].push(process);
  });

  return categorized;
}

export function resolveFormKey(camundaFormKey: string, availableForms: string[]): string | null {
  if (!camundaFormKey) return null;
  if (availableForms.length === 0) return camundaFormKey;

  const normalizedRequested = normalizeKey(camundaFormKey);
  const byNormalized = new Map<string, string>();

  availableForms.forEach((form) => {
    byNormalized.set(normalizeKey(form), form);
  });

  const exact = byNormalized.get(normalizedRequested);
  if (exact) return exact;

  const aliasTarget = FORM_KEY_ALIASES.get(normalizedRequested);
  if (aliasTarget) {
    const resolvedAlias = byNormalized.get(normalizeKey(aliasTarget));
    if (resolvedAlias) return resolvedAlias;
  }

  return null;
}
